import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import withErrorHandler from '../../../hoc/with-error-handler';
import { AppDispatch, RootState } from '../../../store';
import axios from '../../axios';
import { siteUrls } from '../../constants';
import { RemainderNotificationResponse, SmsUpdatesResponse, UnreadMessageResponse } from '../../models';
import * as actions from '../../store/actions';

const WebSocketComponent = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { bufferOfMessageToSend } = useSelector((state: RootState) => state.webSocket);

  const handleMessage = useCallback(
    (event: WebSocketEventMap['message']) => {
      try {
        const msg = JSON.parse(event.data) as RemainderNotificationResponse | UnreadMessageResponse | SmsUpdatesResponse;

        if (!msg) return;

        switch (true) {
          case (msg as RemainderNotificationResponse).isRemainderNotification:
            dispatch(actions.setRemainderNotificationMessage(msg as RemainderNotificationResponse));
            break;
          case (msg as UnreadMessageResponse).isUnreadMessage:
            dispatch(actions.setUnreadMessage(msg as UnreadMessageResponse));
            break;
          case (msg as SmsUpdatesResponse).isSms:
            dispatch(actions.setSmsMessage(msg as SmsUpdatesResponse));
            break;
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    },
    [dispatch],
  );

  const { sendJsonMessage, getWebSocket, readyState } = useWebSocket(siteUrls.messageBrokerWebSocket, {
    shouldReconnect: () => true,
    reconnectInterval: 3000,
    reconnectAttempts: 10,
    onMessage: handleMessage,
    protocols: undefined,
  });

  useEffect(() => {
    if (readyState === ReadyState.OPEN && Array.isArray(bufferOfMessageToSend) && bufferOfMessageToSend.length) {
      const newMessages = [...bufferOfMessageToSend];
      dispatch(actions.clearBufferOfMessageToSend());

      newMessages.forEach((message) => {
        try {
          sendJsonMessage(message);
        } catch (error) {
          console.error('Failed to send message:', error);
          dispatch(actions.addMessageToBuffer(message));
        }
      });
    }
  }, [bufferOfMessageToSend, readyState, sendJsonMessage]);

  useEffect(() => {
    const ws = getWebSocket();
    return () => {
      if (ws?.readyState === ReadyState.OPEN) {
        ws.close();
      }
    };
  }, []);

  return null;
};

export default withErrorHandler(WebSocketComponent, axios);
